// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { components } from 'react-select'

import Form, { useFormContext } from '@ttn-lw/components/form'
import Select from '@ttn-lw/components/select'
import Checkbox from '@ttn-lw/components/checkbox'

import RequireRequest from '@ttn-lw/lib/components/require-request'

import Pill from '@console/components/pill/index.tti'

import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import { getLabelList } from '@console/store/actions/labels.tti'

import { selectLabels } from '@console/store/selectors/labels.tti'

import selectStyles from './select-styles.tti'

import style from './label-select.tti.styl'

const customOption = props => (
  <components.Option {...props}>
    <Checkbox
      name={props.data.value}
      label=""
      value={props.isSelected}
      className="d-flex al-center gap-cs-xs fw-bold mt-0"
    >
      <Pill color={props.data._meta?.color} text={props.data.label} />
    </Checkbox>
  </components.Option>
)

const LabelSuggest = ({ name, initialOptions, ...rest }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const labelsList = useSelector(selectLabels)
  const { values } = useFormContext()
  const [showSuggestions, setShowSuggestions] = useState(labelsList.length !== 0)
  const searchResultsRef = useRef()
  searchResultsRef.current = labelsList
  const noOptionsMessage = useCallback(
    () => formatMessage(sharedMessages.noMatchingLabelFound),
    [formatMessage],
  )

  const handleLoadingOptions = useCallback(
    async value => {
      if (Boolean(value)) {
        try {
          await dispatch(
            attachPromise(
              getLabelList(
                { query: value, order: 'label_id' },
                ['name', 'description', 'entities', 'color'],
                {
                  isSearch: true,
                  all: true,
                },
              ),
            ),
          )
          setShowSuggestions(searchResultsRef?.current?.length !== 0)
          const newOptions = searchResultsRef?.current?.map(label => ({
            value: label.ids.label_id,
            label: label.name,
            _meta: { color: label.color },
          }))

          const translatedOptions = newOptions?.map(option => {
            const { label, labelValues = {} } = option
            if (typeof label === 'object' && label.id && label.defaultMessage) {
              return { ...option, label: formatMessage(label, labelValues) }
            }

            return option
          })

          return translatedOptions
        } catch (error) {
          setShowSuggestions(false)
          return []
        }
      }
    },
    [dispatch, searchResultsRef, formatMessage],
  )

  return (
    <>
      <Form.Field
        {...rest}
        name={name}
        defaultOptions={initialOptions}
        component={Select.Multi}
        noOptionsMessage={noOptionsMessage}
        loadOptions={handleLoadingOptions}
        openMenuOnFocus
        customComponents={{ Option: customOption }}
        maxMenuHeight={300}
        showSuggestions={showSuggestions}
        value={values[name] ? values[name].map(label => ({ value: label, label })) : null}
        hideSelectedOptions={false}
        className={style.labelSelect}
        styles={selectStyles}
        encode={value => value.map(v => v.value)}
      />
    </>
  )
}

LabelSuggest.propTypes = {
  initialOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  name: PropTypes.string.isRequired,
}

LabelSuggest.defaultProps = {
  initialOptions: [],
}

const LabelSelect = ({ name, ...rest }) => {
  const labelsList = useSelector(selectLabels)
  const initialOptions = labelsList.map(label => ({
    value: label.ids.label_id,
    label: label.name,
    _meta: { color: label.color },
  }))

  return (
    <RequireRequest
      requestAction={getLabelList(
        { order: 'label_id' },
        ['name', 'description', 'entities', 'color'],
        {
          all: true,
        },
      )}
      handleErrors={false}
    >
      <LabelSuggest {...rest} name={name} initialOptions={initialOptions} />
    </RequireRequest>
  )
}

LabelSelect.propTypes = {
  name: PropTypes.string.isRequired,
}

export default LabelSelect
